/** @file Curves toolbar: presets for the selected keys, copy shape, normalize toggle, frame all. */
import type { Curve } from '@pose2d/format/types';
import { useStore } from 'zustand';
import { useT } from '../../shell/i18n/useT';
import { Button } from '../../ui-kit/Button';
import { activeCurveKey } from './activeCurveKey';
import { curveEaseShape } from './curveEaseShape';
import type { CurvePorts } from './curvePorts';
import { CURVE_PRESETS } from './curvePresets.data';
import { curveSetCalls } from './curveSetCalls';
import { curveViewStore, type CurveViewState } from './curveViewStore';
import { toCurveRef } from './toCurveRef';
import type { CurveModel } from './useCurveModel';

/** The active key's segment shape (null: flat bezier, no shape; undefined: no active key among the shown tracks). */
function activeShape(model: CurveModel, active: CurveViewState['active']): Curve | null | undefined {
  const at = activeCurveKey(model.tracks, active);
  const track = at === null ? undefined : model.tracks[at.track];
  return track === undefined || at === null ? undefined : curveEaseShape(track, at.key);
}

/** Every button acts on the selected numeric keys as one commit; nothing is sent when no such key is selected. */
export function CurvesToolbar({ model, ports }: { model: CurveModel; ports: CurvePorts }) {
  const t = useT();
  const normalized = useStore(curveViewStore, (s) => s.normalized);
  const shape = activeShape(model, useStore(curveViewStore, (s) => s.active));
  const copy = shape ?? null;
  const none = model.doc === null || !model.keys.some((k) => toCurveRef(k) !== null);
  const send = (curve: Curve): void => {
    const calls = model.doc === null ? [] : curveSetCalls(model.doc, model.keys, curve);
    if (calls.length > 0) ports.commit(calls);
  };
  const flat = shape === null ? { title: t('curves.copyShapeFlat') } : {};
  return (
    <div className="p2-curves-toolbar" role="toolbar" aria-label={t('curves.toolbar')}>
      {CURVE_PRESETS.map((preset) => (
        <Button key={preset.id} label={t(preset.label)} disabled={none} onClick={() => { send(preset.curve); }} />
      ))}
      <Button label={t('curves.copyShape')} disabled={none || copy === null} {...flat} onClick={() => { if (copy !== null) send(copy); }} />
      <Button label={normalized ? t('curves.showValues') : t('curves.normalize')} onClick={() => { curveViewStore.setState({ normalized: !normalized, fitted: false }); }} />
      <Button label={t('curves.frameAll')} onClick={() => { curveViewStore.setState({ fitted: false }); }} />
    </div>
  );
}
